import React, { useState, useEffect } from 'react';

function Customers() {
  const [customers, setCustomers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [sipStatus, setSipStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    balance: 0,
    credit_limit: 0,
    rate_card_id: '',
    provider_id: '',
    status: 'active',
    sip_username: '',
    sip_password: ''
  });

  useEffect(() => {
    fetchCustomers();
    fetchProviders();
    fetchSipStatus();
    
    // Poll SIP status every 10 seconds
    const interval = setInterval(fetchSipStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchCustomers = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProviders(data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchSipStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sip/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSipStatus(data.sip_status || {});
    } catch (error) {
      console.error('Error fetching SIP status:', error);
    }
  };

  const getSipStatusIcon = (sipUsername) => {
    const status = sipStatus[sipUsername];
    if (status === 'online') {
      return <span style={{ color: '#22c55e', fontSize: '1.2em' }} title="Registered">●</span>;
    } else if (status === 'offline') {
      return <span style={{ color: '#ef4444', fontSize: '1.2em' }} title="Offline">●</span>;
    }
    return <span style={{ color: '#94a3b8', fontSize: '1.2em' }} title="Unknown">●</span>;
  };

  const getProviderName = (providerId) => {
    const provider = providers.find(p => p.provider_id === providerId);
    return provider ? provider.name : 'N/A';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      if (editMode) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${currentCustomer.customer_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          alert('Customer updated successfully');
          fetchCustomers();
          setShowModal(false);
        }
      } else {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          alert('Customer created successfully');
          fetchCustomers();
          setShowModal(false);
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      alert('Error saving customer');
    }
  };

  const handleEdit = (customer) => {
    setEditMode(true);
    setCurrentCustomer(customer);
    setFormData({
      company_name: customer.company_name,
      contact_person: customer.contact_person,
      email: customer.email,
      balance: customer.balance,
      credit_limit: customer.credit_limit,
      rate_card_id: customer.rate_card_id,
      provider_id: customer.provider_id || '',
      status: customer.status,
      sip_username: customer.sip_username,
      sip_password: customer.sip_password
    });
    setShowModal(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Customer deleted successfully');
        fetchCustomers();
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Error deleting customer');
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setCurrentCustomer(null);
    setFormData({
      company_name: '',
      contact_person: '',
      email: '',
      balance: 0,
      credit_limit: 0,
      rate_card_id: '',
      provider_id: providers.length > 0 ? providers[0].provider_id : '',
      status: 'active',
      sip_username: '',
      sip_password: ''
    });
    setShowModal(true);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>Customers</h2>
        <button onClick={openAddModal} className="btn-primary">Add Customer</button>
      </div>

      {/* FIXED: Responsive table wrapper with horizontal scroll */}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table style={{ minWidth: '1200px' }}>
          <thead>
            <tr>
              <th>SIP STATUS</th>
              <th>COMPANY</th>
              <th>CONTACT</th>
              <th>EMAIL</th>
              <th>BALANCE</th>
              <th>CREDIT</th>
              <th>PROVIDER</th>
              <th>SIP USER</th>
              <th>SIP PASS</th>
              <th>STATUS</th>
              <th style={{ position: 'sticky', right: 0, background: 'white', boxShadow: '-2px 0 5px rgba(0,0,0,0.1)' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr key={customer.customer_id}>
                <td style={{ textAlign: 'center' }}>
                  {getSipStatusIcon(customer.sip_username)}
                </td>
                <td>{customer.company_name}</td>
                <td>{customer.contact_person}</td>
                <td style={{ fontSize: '0.875rem' }}>{customer.email}</td>
                <td style={{ color: customer.balance <= 0 ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                  ${customer.balance.toFixed(2)}
                </td>
                <td>${customer.credit_limit.toFixed(2)}</td>
                <td>{getProviderName(customer.provider_id)}</td>
                <td className="sip-credential">{customer.sip_username}</td>
                <td className="sip-credential">{customer.sip_password}</td>
                <td>
                  <span className={`badge badge-${customer.status}`}>
                    {customer.status.toUpperCase()}
                  </span>
                </td>
                <td style={{ position: 'sticky', right: 0, background: 'white', whiteSpace: 'nowrap' }}>
                  <button onClick={() => handleEdit(customer)} className="btn-action btn-edit">Edit</button>
                  <button onClick={() => handleDelete(customer.customer_id)} className="btn-action btn-delete">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Customer' : 'Add Customer'}</h3>
              <button onClick={() => setShowModal(false)} className="modal-close">&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Company Name *</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Contact Person *</label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Provider *</label>
                  <select
                    value={formData.provider_id}
                    onChange={(e) => setFormData({...formData, provider_id: e.target.value})}
                    required
                  >
                    <option value="">Select Provider</option>
                    {providers.map(provider => (
                      <option key={provider.provider_id} value={provider.provider_id}>
                        {provider.name} ({provider.sip_server})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Balance</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.balance}
                    onChange={(e) => setFormData({...formData, balance: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>Credit Limit</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({...formData, credit_limit: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Rate Card ID</label>
                  <input
                    type="text"
                    value={formData.rate_card_id}
                    onChange={(e) => setFormData({...formData, rate_card_id: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              {editMode && (
                <>
                  <div className="form-group">
                    <label>SIP Username</label>
                    <input
                      type="text"
                      value={formData.sip_username}
                      onChange={(e) => setFormData({...formData, sip_username: e.target.value})}
                      readOnly={!editMode}
                    />
                  </div>
                  <div className="form-group">
                    <label>SIP Password</label>
                    <input
                      type="text"
                      value={formData.sip_password}
                      onChange={(e) => setFormData({...formData, sip_password: e.target.value})}
                      readOnly={!editMode}
                    />
                  </div>
                </>
              )}

              <div className="modal-footer" style={{ position: 'sticky', bottom: 0, background: 'white', padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editMode ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Customers;
